/**
 * Page lifecycle, collapsed from four browser events into three phases.
 *
 * The events are `visibilitychange`, `pagehide`, `blur` and `focus`, and they do
 * not line up with each other:
 *
 * - Switching tabs fires `visibilitychange` **and** `blur`. Both mean the same
 *   thing, so the pair has to collapse to one `"hidden"` — the consumer of this
 *   port flushes a checkpoint, conceals the Journal and revokes the foreground
 *   lease on that phase, and doing each of those twice is at best wasted work
 *   and at worst a revoke racing a re-grant.
 * - Focusing another window without hiding the tab fires `blur` alone. It still
 *   counts as hidden here, because the app-switcher and window-preview
 *   redaction requirement (spec §5) is satisfied by the concealment curtain and
 *   nothing else can see it coming.
 * - `focus` may arrive while the document is genuinely hidden. It is resolved
 *   against `document.visibilityState` rather than trusted, so a background tab
 *   regaining keyboard focus cannot un-conceal the Journal.
 *
 * Termination comes from `pagehide` and **never** `beforeunload`: mobile Safari
 * does not reliably fire the latter, and the flush is precisely the work that
 * would be lost. `beforeunload` is not listened for at all, so nothing can come
 * to depend on it.
 */

import type { Cancel, LifecyclePhase, VisibilityPort } from "@ff/domain/ports";

interface LifecycleDocumentLike {
  readonly visibilityState: string;
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
}

interface LifecycleWindowLike {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
}

export interface VisibilityEnvironment {
  /** `undefined` outside a document — a worker has no visibility to report. */
  readonly document: LifecycleDocumentLike | undefined;
  readonly window: LifecycleWindowLike | undefined;
}

export function browserVisibilityEnvironment(): VisibilityEnvironment {
  return {
    document: globalThis.document as LifecycleDocumentLike | undefined,
    window: globalThis.window as LifecycleWindowLike | undefined,
  };
}

export class BrowserVisibility implements VisibilityPort {
  readonly #environment: VisibilityEnvironment;
  #phase: LifecyclePhase;
  #listeners: Array<(phase: LifecyclePhase) => void> = [];
  #detach: Array<() => void> = [];

  constructor(environment: VisibilityEnvironment = browserVisibilityEnvironment()) {
    this.#environment = environment;
    this.#phase = documentPhase(environment.document);

    // Attached at construction rather than on first subscription: `current()`
    // has to be right for a subscriber that joins late, and it cannot be if the
    // transitions before it were never observed.
    const documentTarget = environment.document;
    if (documentTarget !== undefined) {
      this.#listen(documentTarget, "visibilitychange", () => {
        this.#settle(documentPhase(documentTarget));
      });
    }

    const windowTarget = environment.window;
    if (windowTarget !== undefined) {
      this.#listen(windowTarget, "pagehide", () => {
        this.#settle("terminating");
      });
      this.#listen(windowTarget, "blur", () => {
        this.#settle("hidden");
      });
      this.#listen(windowTarget, "focus", () => {
        // Resolved, not assumed: focus in a hidden document is still hidden.
        this.#settle(documentPhase(documentTarget));
      });
    }
  }

  current(): LifecyclePhase {
    return this.#phase;
  }

  subscribe(listener: (phase: LifecyclePhase) => void): Cancel {
    this.#listeners.push(listener);
    return () => {
      this.#listeners = this.#listeners.filter((candidate) => candidate !== listener);
    };
  }

  /**
   * Releases the DOM listeners. Not part of the port: a document keeps this for
   * its whole life, but a popout that closes — and a test that builds one per
   * case — would otherwise leave listeners on a shared `window`.
   */
  dispose(): void {
    for (const detach of this.#detach) detach();
    this.#detach = [];
    this.#listeners = [];
  }

  #listen(
    target: LifecycleDocumentLike | LifecycleWindowLike,
    type: string,
    handler: () => void,
  ): void {
    const listener = (): void => {
      handler();
    };
    target.addEventListener(type, listener);
    this.#detach.push(() => {
      target.removeEventListener(type, listener);
    });
  }

  #settle(phase: LifecyclePhase): void {
    // Nothing follows termination. The document is going away, and a callback
    // after `pagehide` runs against a tree that is already being torn down.
    if (this.#phase === "terminating") return;
    if (phase === this.#phase) return;

    this.#phase = phase;
    if (phase === "terminating") {
      // The subscriber list stays — `current()` must keep answering — but there
      // is nothing left for the DOM to tell us.
      for (const detach of this.#detach) detach();
      this.#detach = [];
    }

    // A snapshot: the first handler routinely tears down a surface whose own
    // handler is still in the list, and iterating the live array would skip it.
    for (const listener of [...this.#listeners]) listener(phase);
  }
}

function documentPhase(target: LifecycleDocumentLike | undefined): LifecyclePhase {
  // Without a document there is nothing to hide, and reporting `"hidden"` would
  // stop a worker-hosted consumer before it started.
  if (target === undefined) return "visible";
  return target.visibilityState === "hidden" ? "hidden" : "visible";
}
